import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI agent design coach evaluating a learner's agent spec and build.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

SPEC COMPLETENESS: Are all 8 spec fields answered with enough detail to configure a real agent? (1-4)

INSTRUCTION QUALITY: Are the step-by-step instructions complete, unambiguous, and sequenced? (1-4)

GUARDRAILS AND ESCALATION: Are there specific guardrails and a precise escalation condition? (1-4)

BUILD COMPLETION: Was the agent fully configured including guardrails and output routing? (1-4)

TEST COVERAGE: Were all three tests run with documented results and fixes applied? (1-4)

After scoring:
1. Give a total score out of 20.
2. Identify the single most likely failure point in the current spec or build.
3. Write the specific instruction or guardrail that would prevent that failure.
4. Name one real-world scenario where this agent would produce the wrong result, and what the fix is.

Here is my agent spec and test results:

[PASTE YOUR COMPLETE SPEC AND ALL THREE TEST RESULT LOGS]`;

const RUBRIC_ROWS = [
  { criterion: 'SPEC COMPLETENESS', missing: '4 or fewer fields answered', partial: '5-6 fields answered', solid: '7 fields answered', excellent: 'All 8 fields answered before build started' },
  { criterion: 'INSTRUCTION QUALITY', missing: 'Instructions vague or missing steps', partial: 'Major steps present, some gaps', solid: 'All steps present and sequenced', excellent: 'Step-by-step, complete, ready to configure' },
  { criterion: 'GUARDRAILS AND ESCALATION', missing: 'No guardrails or escalation logic', partial: 'One constraint, no escalation', solid: 'Meaningful guardrails with escalation named', excellent: 'Two+ specific constraints and a precise escalation trigger' },
  { criterion: 'BUILD COMPLETION', missing: 'No build attempted', partial: 'Trigger set only', solid: 'Trigger plus actions, no guardrails', excellent: 'Fully configured with guardrails and output routing' },
  { criterion: 'TEST COVERAGE', missing: 'No tests run', partial: 'Standard test only', solid: 'Standard and edge case', excellent: 'All three tests run with documented results and fixes applied' },
];

const BUILD_STEPS = [
  'Create workflow and name it',
  'Set and test the trigger',
  'Add step 1 action',
  'Add step 2 action',
  'Add step 3 action',
  'Connect and authenticate all tools',
  'Set guardrail conditions',
  'Set output destination and routing',
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay7({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day7'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day7: { ...(existing.progress?.day7 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day7: { ...(existing.progress?.day7 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day7: { ...(existing.progress?.day7 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_spec', 'score_instructions', 'score_guardrails', 'score_build', 'score_tests']);

  const SPEC_FIELDS = [
    { num: '01', name: 'Agent Name', key: 'spec_name', hint: 'Specific enough that someone else knows exactly what it does.', height: 60, type: 'text' },
    { num: '02', name: 'Agent Type', key: 'spec_type', hint: 'Notification / Action / Conversational', height: 60, type: 'radio' },
    { num: '03', name: 'Trigger Event', key: 'spec_trigger', hint: 'The exact condition. What event, from which system, with what criteria?', height: 70, type: 'text' },
    { num: '04', name: 'Step-by-Step Instructions', key: 'spec_instructions', hint: 'Numbered. Every step before the agent stops. No skipped steps. Write it as if someone who has never seen this process will configure it from your description.', height: 110, type: 'text' },
    { num: '05', name: 'Tools and Access', key: 'spec_tools', hint: 'List each tool with access type: read / write / send. If you need to authenticate, note that here.', height: 80, type: 'text' },
    { num: '06', name: 'Output Destination', key: 'spec_output', hint: 'Where does the result go? Which system, which channel, which document? Who sees it first?', height: 70, type: 'text' },
    { num: '07', name: 'Guardrail Conditions', key: 'spec_guardrails', hint: 'At minimum: what must the agent NOT do, and under what condition should it stop and wait for human input?', height: 80, type: 'text' },
    { num: '08', name: 'Escalation Logic', key: 'spec_escalation', hint: 'What triggers a handoff to a human? Who receives it? How: email, Slack, task assignment?', height: 70, type: 'text' },
  ];

  const TEST_SECTIONS = [
    {
      key: 'standard',
      label: 'Standard Test',
      sub: 'Fire the trigger with a normal, expected input.',
      fields: [
        { key: 'input', label: 'What input did I use?' },
        { key: 'did', label: 'What did the agent do?' },
        { key: 'landed', label: 'Did the output land in the right place?' },
        { key: 'guardrail', label: 'Did any guardrail fire unexpectedly?' },
      ],
    },
    {
      key: 'edge',
      label: 'Edge Case Test',
      sub: 'Fire the trigger with something slightly outside the expected: a borderline input, an unusual condition.',
      fields: [
        { key: 'case', label: 'What edge case did I test?' },
        { key: 'did', label: 'What did the agent do with it?' },
        { key: 'guardrail', label: 'Did the guardrail catch it correctly?' },
        { key: 'fix', label: 'What did I need to fix?' },
      ],
    },
    {
      key: 'break',
      label: 'Break Test',
      sub: 'Try to make the agent fail: wrong input type, missing data, incomplete trigger.',
      fields: [
        { key: 'how', label: 'How did I try to break it?' },
        { key: 'happened', label: 'What actually happened?' },
        { key: 'graceful', label: 'Did it fail gracefully or produce bad output?' },
        { key: 'add', label: 'What guardrail or instruction needs to be added?' },
      ],
    },
  ];

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 7 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={7} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 7</div>
        <div style={s.headerTitle}>Agent Building</div>
        <div style={s.headerDesc}>Spec it before you build it. Test it before you trust it.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Building an agent is four steps in order: Brief (your four ingredients from Day 6), Spec (translating the brief into platform language before touching any tool), Build (configuring the agent in a no-code platform), and Test (standard, edge case, and break tests to find gaps before they find you). The spec is the most skipped step and the most important one. It's what prevents you from discovering mid-build that your instructions are incomplete. Eight questions in the spec: agent name, agent type, trigger event, step-by-step instructions, tools and access, output destination, guardrail conditions, and escalation logic. Don't open any tool until all eight are answered.
        </div>
      </div>

      <Section title="Part 1 — Complete Your Agent Spec">
        <div style={s.exerciseIntro}>Complete all eight fields before opening any build platform. The spec is your design document. Incomplete specs produce broken agents.</div>

        <div style={s.specBlock}>
          {SPEC_FIELDS.map((field, i, arr) => (
            <div key={field.key} style={{ ...s.specRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.specLabel}>
                <div style={s.specNum}>{field.num}</div>
                <div style={s.specName}>{field.name}</div>
                <div style={s.specHint}>{field.hint}</div>
              </div>
              {field.type === 'radio' ? (
                <div style={{ flex: 1, padding: '12px', background: '#FAF8F5', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {['Notification', 'Action', 'Conversational'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
                      <input type="radio" name="spec_agent_type" checked={data[field.key] === opt} onChange={() => set(field.key, opt)} /> {opt}
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  style={{ ...s.textarea, flex: 1, border: 'none', minHeight: field.height, background: '#FAF8F5' }}
                  value={data[field.key] || ''}
                  onChange={e => set(field.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Part 2 — Build Log">
        <div style={s.exerciseIntro}>As you build, log each step. This becomes your troubleshooting record if anything goes wrong.</div>
        <Field label="Platform I'm using:" value={data.platform} onChange={v => set('platform', v)} minHeight={44} />

        <div style={s.buildTable}>
          <div style={s.buildHeader}>
            <div style={{ ...s.buildHCol, width: '36px', flexShrink: 0 }}>#</div>
            <div style={{ ...s.buildHCol, flex: 1 }}>Build Step</div>
            <div style={{ ...s.buildHCol, width: '130px', flexShrink: 0 }}>Status</div>
            <div style={{ ...s.buildHCol, flex: 1, borderRight: 'none' }}>Notes / Issues</div>
          </div>
          {BUILD_STEPS.map((step, i, arr) => (
            <div key={i} style={{ ...s.buildRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.buildNum}>{i + 1}</div>
              <div style={{ ...s.buildCell, flex: 1, padding: '11px', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', display: 'flex', alignItems: 'center' }}>{step}</div>
              <div style={{ ...s.buildCell, width: '130px', flexShrink: 0 }}>
                <div style={s.buildStatus}>
                  {['Done', 'Blocked', 'Skipped'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer' }}>
                      <input type="radio" name={`bs_${i}`} checked={data[`bs_${i}_status`] === opt} onChange={() => set(`bs_${i}_status`, opt)} /> {opt}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ ...s.buildCell, flex: 1, borderRight: 'none' }}>
                <textarea style={s.buildInput} value={data[`bs_${i}_notes`] || ''} onChange={e => set(`bs_${i}_notes`, e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Part 3 — Test Results">
        <div style={s.exerciseIntro}>Run all three tests. Document every result. The failures are more useful than the successes.</div>

        {TEST_SECTIONS.map((test, ti) => (
          <div key={test.key} style={{ ...s.testBlock, marginBottom: ti < TEST_SECTIONS.length - 1 ? '14px' : '0' }}>
            <div style={s.testHeader}>{test.label}</div>
            <div style={s.testSub}>{test.sub}</div>
            {test.fields.map((f, fi) => (
              <div key={f.key} style={{ ...s.testField, borderBottom: fi < test.fields.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
                <div style={s.fieldLabel}>{f.label}</div>
                <textarea style={{ ...s.textarea, minHeight: fi === 1 ? 66 : 50 }} value={data[`${test.key}_${f.key}`] || ''} onChange={e => set(`${test.key}_${f.key}`, e.target.value)} />
              </div>
            ))}
          </div>
        ))}
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate your completed agent spec and build.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your agent spec and build:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Spec" value={data.score_spec} onChange={v => set('score_spec', v)} />
          <ScoreField label="Instructions" value={data.score_instructions} onChange={v => set('score_instructions', v)} />
          <ScoreField label="Guardrails" value={data.score_guardrails} onChange={v => set('score_guardrails', v)} />
          <ScoreField label="Build" value={data.score_build} onChange={v => set('score_build', v)} />
          <ScoreField label="Tests" value={data.score_tests} onChange={v => set('score_tests', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this along with your complete spec and test log.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     Most likely failure point:" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={66} />
        <Field label="Specific instruction or guardrail that would prevent it:" value={data.ai_prevent} onChange={v => set('ai_prevent', v)} minHeight={66} />
      </Section>

      <CalloutBox title="THE STANDARD: 16 / 20">
        Keep working until your agent spec scores 16/20 or higher AND you've run all three tests with documented results. If the break test revealed a gap in your guardrails, fix it before you close this book.
      </CalloutBox>

      <Section title="Reflection">
        <Field
          label="1. What was the hardest spec field to complete, and what does that reveal about the complexity of the process you chose to automate?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. What did the break test reveal that the standard test did not? What does that tell you about where human judgment still matters?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 7">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 8 — YOUR ASSIGNMENT">
        <span>Run your agent on real input at least once. Then:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Log the real-world run: what input triggered it, what the agent did, where the output landed.</li>
          <li>Note what worked exactly as designed.</li>
          <li>Note what needed a fix. Be specific about which instruction or guardrail was the issue.</li>
          <li>Write the specific change you would make to one instruction and one guardrail based on what you saw.</li>
          <li>Bring all four observations to Day 8. That's your iteration brief.</li>
        </ol>
      </CalloutBox>
      <Field label="The one thing I would change based on the real-world run:" value={data.assignment_change} onChange={v => set('assignment_change', v)} minHeight={66} />

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 7 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 7 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 7 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '18px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  specBlock: { border: '1.5px solid #E8E6E2' },
  specRow: { display: 'flex' },
  specLabel: { width: '160px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  specNum: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A', marginBottom: '3px' },
  specName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  specHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.4' },
  buildTable: { border: '1.5px solid #E8E6E2' },
  buildHeader: { display: 'flex', background: '#1B2A4A' },
  buildHCol: { padding: '9px 11px', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  buildRow: { display: 'flex' },
  buildNum: { width: '36px', flexShrink: 0, background: '#F2F0EC', borderRight: '1px solid #E8E6E2', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14px', fontSize: '12px', fontWeight: '700', color: '#1B2A4A' },
  buildCell: { borderRight: '1px solid #E8E6E2' },
  buildStatus: { padding: '10px 11px', background: '#FAF8F5', display: 'flex', flexDirection: 'column', gap: '6px' },
  buildInput: { width: '100%', border: 'none', padding: '9px 11px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '60px', lineHeight: '1.5', boxSizing: 'border-box', display: 'block' },
  testBlock: { border: '1.5px solid #E8E6E2' },
  testHeader: { background: '#1B2A4A', color: '#D4A847', padding: '10px 14px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  testSub: { background: '#F2F0EC', padding: '9px 14px', fontSize: '12px', color: '#3D3D3D', fontStyle: 'italic', borderBottom: '1px solid #E8E6E2' },
  testField: { padding: '10px 14px' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
